/**
 * F-075 — Texas social media scheduler (E-022 FE, Batch-8).
 *
 * LinkedIn binds to the EXISTING canonical LP Texas organic engine
 * (src/routes/linkedin.js, engine src/lib/linkedin-scheduler.js):
 *   GET  /api/linkedin/texas/post?date=YYYY-MM-DD -> LinkedInTexasPost | null
 *   POST /api/linkedin/texas/post | /review      -> lifecycle gate
 * Twitter has no published batch-8 route yet, so queueing binds to the
 * nominated path SOCIAL_PATHS.twitterQueue and degrades to an empty queue
 * until buffy's contract lands.
 */
import { linkedinOrganicApi, LinkedInTexasPost, LinkedInReviewAction } from './linkedinOrganic';
import { landlordApi } from './api';

export type SocialChannel = 'linkedin' | 'twitter';

export type SocialStatus = 'draft' | 'pending_review' | 'approved' | 'published' | 'rejected' | 'queued' | 'unsent';

export interface ScheduledPost {
    id: string;
    channel: SocialChannel;
    content: string;
    date: string;
    status: SocialStatus;
    review?: LinkedInTexasPost['review'];
    source?: string;
}

export interface TxSocialTemplate {
    id: string;
    channel: SocialChannel | 'any';
    label: string;
    text: string;
    tags: string[];
}

export const TX_SOCIAL_TEMPLATES: TxSocialTemplate[] = [
    {
        id: 'sb38',
        channel: 'any',
        label: 'SB 38 notice timing',
        text: 'SB 38 changed when a Texas notice is served — and who has to prove it. Track every notice window per tenancy, with the evidence pack attached. #TexasLandlords #SB38',
        tags: ['SB38', 'Texas'],
    },
    {
        id: 'houston-ch10',
        channel: 'any',
        label: 'Houston Ch. 10 ASEC',
        text: 'Houston Ch. 10 Art. II owners: the Annual Self-Inspection Certification is not optional. File the ASEC, close the deficiencies, keep the evidence. #HoustonASEC',
        tags: ['Houston', 'Ch10'],
    },
    {
        id: 'dallas-ch27',
        channel: 'any',
        label: 'Dallas Ch. 27 registration',
        text: 'Dallas Ch. 27 rental registration renews every year. Miss the window and the fee is the cheap part. Portfolio-wide registration posture, one screen. #DallasCh27',
        tags: ['Dallas', 'Ch27'],
    },
    {
        id: 'us-residency',
        channel: 'any',
        label: 'US data residency',
        text: 'Your Texas tenant records should not leave the United States. US data residency is the default, not a line item. #DataResidency #Texas',
        tags: ['Residency'],
    },
];

export function isoDate(d: Date): string {
    const month = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${d.getFullYear()}-${month}-${day}`;
}

export function addDays(d: Date, days: number): Date {
    const next = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    next.setDate(next.getDate() + days);
    return next;
}

/** Seven local dates starting at `start` (defaults to today). */
export function weekDays(start?: Date): string[] {
    const base = start || new Date();
    return Array.from({ length: 7 }, (_, i) => isoDate(addDays(base, i)));
}

export function groupByDate(posts: ScheduledPost[]): Record<string, ScheduledPost[]> {
    return posts.reduce<Record<string, ScheduledPost[]>>((acc, post) => {
        (acc[post.date] = acc[post.date] || []).push(post);
        return acc;
    }, {});
}

/** Scheduled-but-unsent: queued for review or approved but not yet published. */
export function unsentPosts(posts: ScheduledPost[]): ScheduledPost[] {
    return posts.filter((post) => post.status !== 'published' && post.status !== 'rejected');
}

export function mapLinkedInPost(post: LinkedInTexasPost): ScheduledPost {
    const status = String(post.status || 'draft').toLowerCase() as SocialStatus;
    return {
        id: post.id || `linkedin-${post.date}`,
        channel: 'linkedin',
        content: post.content || '',
        date: post.date,
        status,
        review: post.review || undefined,
        source: post.templateName,
    };
}

export const SOCIAL_PATHS = {
    twitterQueue: 'api/social/twitter/queue',
    twitterPost: 'api/social/twitter/post',
} as const;

const unwrapBody = (res: any): any => {
    const body = res?.data;
    if (body && body.success === false) return null;
    return body && body.data !== undefined ? body.data : body;
};

const toPosts = (data: any): ScheduledPost[] => {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.posts)) return data.posts;
    if (data && Array.isArray(data.rows)) return data.rows;
    return [];
};

export const socialApi = {
    /** Seven GETs against the real LinkedIn engine; a 404 date means no post yet. */
    linkedInWeek: (weekStart?: Date): Promise<ScheduledPost[]> => {
        const dates = weekDays(weekStart);
        return Promise.all(dates.map((date) => linkedinOrganicApi.getPost(date).catch(() => null)))
            .then((posts) => posts.filter(Boolean) as LinkedInTexasPost[])
            .then((posts) => posts.map(mapLinkedInPost));
    },

    twitterQueue: (weekStart?: Date): Promise<ScheduledPost[]> =>
        landlordApi
            .get(SOCIAL_PATHS.twitterQueue, { params: weekStart ? { from: isoDate(weekStart), to: isoDate(addDays(weekStart, 6)) } : undefined })
            .then((res: any) => toPosts(unwrapBody(res)))
            .catch(() => []),

    queue: (post: { channel: SocialChannel; content: string; date: string }): Promise<ScheduledPost | null> =>
        landlordApi
            .post(post.channel === 'twitter' ? SOCIAL_PATHS.twitterPost : 'api/linkedin/texas/post', post)
            .then((res: any) => unwrapBody(res))
            .then((data: any) => (data ? (data as ScheduledPost) : null))
            .catch(() => null),
};

export const linkedInActions = {
    review: (date: string, action: LinkedInReviewAction, note?: string) =>
        linkedinOrganicApi.review(date, { action, note }),
};

export default socialApi;
